import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import throttle from 'lodash/throttle';
import { formatBanner } from './helpers/formatBanner';
import type { ILocation } from '@/interface/ILocation';
import { BrandType, ExchangeTransferStatus, FantasySelectionOrderStatus, FeatureEvent, LiveStreamResource, PriceStyle, SportType } from '@/interface/enum';
import type { ITransferRecord } from '@/interface/exchange';
import { JackpotPoolType, TriggerResult } from '@/interface/jackpot';
import type {
    BannerInfo,
    BannerRequest,
    Bet,
    BetBuilderOrderRequest,
    BetBuilderOrderResponse,
    BetBuilderResultResponse,
    BetBuilderTicketRequest,
    BetBuilderTicketResponse,
    CashOutRequest,
    CashOutResponse,
    CashOutValueResponse,
    CashOutValuesRequest,
    ClaimJackpotRequest,
    ConfigurationSettingsParams,
    CustomerInfo,
    DynamicMarketOrderRequest,
    DynamicMarketOrderResponse,
    DynamicMarketResultBySelectionResponse,
    DynamicMarketTicketRequest,
    DynamicMarketTicketResponse,
    DynamicMatchMarketResultResponse,
    ExchangeResponseOfCustomerInfoResponse,
    ExchangeResponseOfObject,
    FantasySelectionBetCountResponse,
    FantasySelectionBetResponse,
    FantasySelectionMyBetDetailResponse,
    FantasySelectionMyBetsResponse,
    FantasySelectionTokenResponse,
    FeatureEventStateResponse,
    GenerateTelegramLinkResponse,
    LiveStreamGetLiveStreamUrlParams,
    LiveStreamResponse,
    LocationResponse,
    MatchResponse,
    MatchResultGetBetBuilderResultParams,
    MatchResultGetDynamicMarketResultBySelectionParams,
    MatchResultGetDynamicMarketResultParams,
    MatchResultGetMatchResultsParams,
    MatchResultGetOutrightTournamentResultsParams,
    MyBetListGetRunningBetCountParams,
    MyBetListMyBetDetailParams,
    MyBetListSettledBetList2Params,
    MyBetListSettledBetListParams,
    MyBetsResponse,
    OrderResponse,
    OutrightTicketInfo,
    PlaceComboParlayOrderRequest,
    PlaceFantasySelectionBetRequest,
    PlaceFantasySelectionBetResponse,
    PlaceOutrightOrderRequest,
    PlaceSingleOrderRequest,
    TriggerResult as PolluxTriggerResult,
    RunningBetCountResponse,
    SetFeatureEventRequest,
    SettingRequest,
    TelegramGenerateTelegramLinkParams,
    TicketInfo,
    TicketMpResponse,
    TicketResponse,
    Tokens,
    TournamentResponse,
    TransferRequest,
    WelcomeCreateSessionParams,
} from '@/interface/pollux';
import {
    EnumProject,
    SiteStyle,
} from '@/interface/pollux';
import { UnexpectedResponseError } from '@/libraries/apiError';
import { createApiInstance } from '@/libraries/apis/createApiInstance';
import { useApp } from '@/libraries/composables/useApp';
import { formatBet, formatMyBets, formatRunningBetCount } from '@/libraries/helpers/betListHelper';
import {
    formatBetBuilderOrder,
    formatBetBuilderTicket,
    formatDynamicMarketOrder,
    formatDynamicMarketTicket,
    formatMpTicket,
    formatOrder,
    formatTicket,
} from '@/libraries/helpers/betSlipHelper';
import { formatBettingPageInfo, formatExchangeCustomerInfo } from '@/libraries/helpers/exchangeHelper';
import { formatBet as formatFantasySelectionBet, formatFantasySelectionBetCount, formatMyBets as formatFantasySelectionMyBets, formatMyBetDetail } from '@/libraries/helpers/fantasySelectionMyBetHelper';
import { formatCashOut, formatCashOutValue } from '@/libraries/helpers/formatCashOut';
import { formatCustomer } from '@/libraries/helpers/formatCustomer';
import { formatFeatureEvents } from '@/libraries/helpers/formatFeatureEvent';
import { formatLiveStream } from '@/libraries/helpers/formatLiveStream';
import { formatOutrightResults } from '@/libraries/helpers/formatOutrightResult';
import { formatBetBuilderResults, formatDynamicMarketResultBySelection, formatDynamicMarketResults, formatResults } from '@/libraries/helpers/formatResult';
import localizationHelper, { Backdoor } from '@/libraries/helpers/localizationHelper';
import { logMissingHttpReferer } from '@/libraries/helpers/missingHttpRefererHelper';
import { platform } from '@/libraries/helpers/platformHelper';
import { getSportsApiAsiUrl, getSportsApiUrl, getSportsAsiUrl, getSportsBsiUrl } from '@/libraries/url';
import { noop } from '@/libraries/utils/utils';

axios.defaults.timeout = 15 * 1000;

const FEATURE_EVENT_TIMEOUT = 500;

function createInstance(baseUrl: string) {
    return createApiInstance({
        baseURL: baseUrl,
        withCredentials: true,
    });
}

export const sportsApiInstance = createInstance(`${getSportsApiUrl()}/api`);

function getRequestLabel(config: AxiosRequestConfig) {
    const method = config.method?.toUpperCase();

    return `${method} ${sportsApiInstance.getUri(config)}`;
}

function addRequestContextToError(error: AxiosError, config: AxiosRequestConfig) {
    error.message = `${error.message} (request: ${getRequestLabel(config)})`;
    return error;
}

export function fireAndForget(request: Promise<unknown>): void {
    request.catch((error) => {
        console.warn(error);
    });
}

const goToLoginPage = throttle(() => {
    window.location.href = `${getSportsApiUrl()}/api/signin-sbo`;
}, 5000, { trailing: false });

let isRedirecting = false;

export function resetRedirectingState() {
    isRedirecting = false;
}

function handleSessionProblem(reasonPhrase: string) {
    switch (reasonPhrase) {
        case 'sessionProblem_multiLogin':
            platform.logout('multipleLogin');
            break;
        case 'sessionProblem_notOnline':
            platform.logout('sessionTimeout');
            break;
        case 'sessionProblem_dbError':
        default:
            platform.logout('errorPromptLogin');
            break;
    }
}

const { isFromApp, goToHomePageInApp } = useApp();

sportsApiInstance.interceptors.response.use(
    (response) => {
        logMissingHttpReferer(response);

        const isUM = response.headers['um-enabled'] === 'y';
        if (isUM) {
            window.location.reload();
            return new Promise(noop);
        }

        return response;
    },
    (error) => {
        logMissingHttpReferer(error.response);

        if (error.response?.status === 401 && __ENV_ASI__) {
            if (isFromApp) {
                goToHomePageInApp(error.response.data?.reasonPhrase);
            }

            if (window.isPpReady && !isRedirecting) {
                isRedirecting = true;
                handleSessionProblem(error.response.data?.reasonPhrase);
                setTimeout(() => window.location.reload(), 7000); // Prevent PP forgot logout.
            }

            const authMethod = error?.response?.headers['Authentication-Method'];

            if (authMethod === 'OAuth') {
                goToLoginPage();
            }
            // If we get 401 in sport ASI, then redirect to BSI
            if (authMethod !== 'OAuth' && window.location.origin === getSportsAsiUrl()) {
                setTimeout(() => {
                    window.location.href = getSportsBsiUrl();
                }, 2000);
            }

            return new Promise(noop); // stop processing 401 responses
        }
        if (axios.isAxiosError(error) && error.config) {
            return Promise.reject(addRequestContextToError(error, error.config));
        }
        return Promise.reject(error);
    },
);

const notDefinedType: unique symbol = Symbol('nonExist');
type NotDefinedType = typeof notDefinedType

type TypedParamsAxiosRequestConfig<TParams> = Omit<AxiosRequestConfig, 'params'> & { params?: TParams }

/** wrapper for `axios.get` with request & response type checking */
function get<TRequest = NotDefinedType, TResponse = NotDefinedType>(
    url: string,
    config?: TypedParamsAxiosRequestConfig<TRequest extends NotDefinedType ? 'You must provide type parameters' : NoInfer<TRequest>>,
): Promise<AxiosResponse<TResponse>> {
    return sportsApiInstance.get<TResponse>(url, config);
}

/** wrapper for `axios.post` with request & response type checking */
function post<TRequest = NotDefinedType, TResponse = NotDefinedType>(
    url: string,
    data?: TRequest extends NotDefinedType ? 'You must provide type parameters' : NoInfer<TRequest>,
    config?: AxiosRequestConfig,
): Promise<AxiosResponse<TResponse>> {
    return sportsApiInstance.post<TResponse>(url, data, config);
}

function isBrokenTokens(data: Tokens): boolean {
    if (typeof data !== 'object' || data === null) return true;
    return !data.authToken || !data.om || !data.sm;
}

export default {
    async getConfiguration() {
        const response = await get<ConfigurationSettingsParams, Record<string, string>>('/configuration/settings', {
            params: {
                project: EnumProject.Castor,
                siteStyle: SiteStyle.Unknown,
            },
        });
        return response.data;
    },
    async createSession(id: string) {
        const response = await get<WelcomeCreateSessionParams>('welcome/createSession', {
            params: {
                id,
            },
        });
        return response.status;
    },
    async logout() {
        await get('welcome/logout', {
            // always call ASI endpoint even if currently in BSI page
            baseURL: `${getSportsApiAsiUrl()}/api`,
        });
    },
    async openSingleTicket(request: TicketInfo, option?: AxiosRequestConfig) {
        const response = await post<TicketInfo, TicketResponse>('singleBetSlip/openTicket', request, option);
        return formatTicket(response.data);
    },
    async openMultipleSingleTicket(request: TicketInfo, option?: AxiosRequestConfig) {
        const response = await post<TicketInfo, TicketResponse>('singleBetSlip/openMultipleSingleTicket', request, option);
        return formatTicket(response.data);
    },
    async openOutrightTicket(request: OutrightTicketInfo, option?: AxiosRequestConfig) {
        const response = await post<OutrightTicketInfo, TicketResponse>('outrightBetSlip/openTicket', request, option);
        return formatTicket(response.data);
    },
    async openMultipleOutrightTicket(request: OutrightTicketInfo, option?: AxiosRequestConfig) {
        const response = await post<OutrightTicketInfo, TicketResponse>('outrightBetSlip/openMultipleTicket', request, option);
        return formatTicket(response.data);
    },
    async openMixParlayTicket(request: TicketInfo[], option: AxiosRequestConfig) {
        const response = await post<TicketInfo[], TicketMpResponse>('parlayBetSlip/openTicket', request, option);
        return formatMpTicket(response.data);
    },
    async openBetBuilderTicket(request: BetBuilderTicketRequest, option: AxiosRequestConfig) {
        const response = await post<BetBuilderTicketRequest, BetBuilderTicketResponse>('betBuilderBetSlip/openTicket', request, option);
        return formatBetBuilderTicket(response.data);
    },
    async openDynamicMarketTicket(selectionId: number, option: AxiosRequestConfig) {
        const request: DynamicMarketTicketRequest = {
            selectionId,
        };
        const response = await post<DynamicMarketTicketRequest, DynamicMarketTicketResponse>('dynamicMarketBetSlip/openTicket', request, option);
        return formatDynamicMarketTicket(response.data);
    },
    async openMultipleDynamicMarketTicket(selectionId: number, option: AxiosRequestConfig) {
        const request: DynamicMarketTicketRequest = {
            selectionId,
        };
        const response = await post<DynamicMarketTicketRequest, DynamicMarketTicketResponse>('dynamicMarketBetSlip/openMultipleSingleTicket', request, option);
        return formatDynamicMarketTicket(response.data);
    },
    async clearTicket() {
        await post('betSlip/clearTicket');
    },
    async placeSingleBet(request: PlaceSingleOrderRequest) {
        const response = await post<PlaceSingleOrderRequest, OrderResponse>('singleBetSlip/placeBet', request);
        return formatOrder(response.data);
    },
    async placeSingleVoucherBet(request: PlaceSingleOrderRequest) {
        const response = await post<PlaceSingleOrderRequest, OrderResponse>('singleBetSlip/placeFreeBet', request);
        return formatOrder(response.data);
    },
    async placeSingleBets(request: PlaceSingleOrderRequest[]) {
        const response = await post<PlaceSingleOrderRequest[], OrderResponse[]>('singleBetSlip/placeBets', request);
        return response.data.map(formatOrder);
    },
    async placeComboParlayBet(request: PlaceComboParlayOrderRequest) {
        const response = await post<PlaceComboParlayOrderRequest, OrderResponse>('parlayBetSlip/placeComboParlay', request);
        return formatOrder(response.data);
    },
    async placeOutrightBet(request: PlaceOutrightOrderRequest) {
        const response = await post<PlaceOutrightOrderRequest, OrderResponse>('outrightBetSlip/placeBet', request);
        return formatOrder(response.data);
    },
    async placeOutrightBets(request: PlaceOutrightOrderRequest[]) {
        const response = await post<PlaceOutrightOrderRequest[], OrderResponse[]>('outrightBetSlip/placeBets', request);
        return response.data.map(formatOrder);
    },
    async placeBetBuilder(request: BetBuilderOrderRequest) {
        const response = await post<BetBuilderOrderRequest, BetBuilderOrderResponse>('betBuilderBetSlip/placeBet', request);
        return formatBetBuilderOrder(response.data);
    },
    async placeBetDynamicMarket(request: DynamicMarketOrderRequest) {
        const response = await post<DynamicMarketOrderRequest, DynamicMarketOrderResponse>('dynamicMarketBetSlip/placeBet', request);
        return formatDynamicMarketOrder(response.data);
    },
    async placeDynamicMarketVoucherBet(request: DynamicMarketOrderRequest) {
        const response = await post<DynamicMarketOrderRequest, DynamicMarketOrderResponse>('dynamicMarketBetSlip/placeFreeBet', request);
        return formatDynamicMarketOrder(response.data);
    },
    async placeDynamicMarketBets(request: DynamicMarketOrderRequest[]) {
        const response = await post<DynamicMarketOrderRequest[], DynamicMarketOrderResponse[]>('dynamicMarketBetSlip/placeBets', request);
        return response.data.map(formatDynamicMarketOrder);
    },
    async getRunningBetCount(isUseCache: boolean) {
        const response = await get<MyBetListGetRunningBetCountParams, RunningBetCountResponse>('myBetList/getRunningBetCount', {
            params: {
                c: isUseCache,
            },
        });
        return formatRunningBetCount(response.data);
    },
    async getMyBets() {
        const response = await get<never, MyBetsResponse>('myBetList/myBetList');
        return formatMyBets(response.data);
    },
    async getSettledBets(winLostDate: string) {
        const response = await get<MyBetListSettledBetListParams, Bet[]>('myBetList/settledBetList', {
            params: {
                winLostDate,
            },
        });
        return response.data.map(bet => formatBet(bet));
    },
    async getCustomerInfo() {
        const response = await get<never, CustomerInfo>('account/getCustomerInfo');

        switch (localizationHelper.getBackdoor()) {
            case Backdoor.India:
                response.data.currency = 'INR';
                break;
            case Backdoor.Brazil:
                response.data.currency = 'BRL';
                break;
            default:
                break;
        }

        return formatCustomer(response.data);
    },
    async getOddsApiTokens() {
        const response = await get<never, Tokens>('oddsApi/getTokens');

        if (isBrokenTokens(response.data)) {
            throw new UnexpectedResponseError(`getOddsApiTokens response: ${JSON.stringify(response.data)}`);
        }

        return response.data;
    },
    async getBalance() {
        const response = await get<never, number>('account/betCredit');
        return response.data;
    },
    async updateSiteStyle(siteStyle: SiteStyle) {
        const response = await post<SettingRequest, CustomerInfo>('userSetting/updateSiteStyle', {
            siteStyle,
        });
        return formatCustomer(response.data);
    },
    async updatePriceStyle(priceStyle: PriceStyle) {
        const response = await post<SettingRequest, CustomerInfo>('userSetting/updatePriceStyle', {
            priceStyle: PriceStyle.toPollux(priceStyle),
        });
        return formatCustomer(response.data);
    },
    async updateIsAcceptAnyOdds(isAcceptAnyOdds: boolean) {
        const response = await post<SettingRequest, CustomerInfo>('userSetting/updateIsAcceptAnyOdds', {
            isAcceptAnyOdds,
        });
        return formatCustomer(response.data);
    },
    async updateIsAcceptAnyCashOutValue(isAcceptAnyCashOutValue: boolean) {
        const response = await post<SettingRequest, CustomerInfo>('userSetting/updateIsAcceptAnyCashOutValue', {
            isAcceptAnyCashOutValue,
        });
        return formatCustomer(response.data);
    },
    async getFeatureEvents() {
        const response = await get<never, FeatureEventStateResponse>('featureEventRecord/getEvents', {
            timeout: FEATURE_EVENT_TIMEOUT,
        });
        return formatFeatureEvents(response.data);
    },
    async setFeatureEvent(event: FeatureEvent) {
        await post<SetFeatureEventRequest, void>('featureEventRecord/setEvent', { event: FeatureEvent.toPollux(event) });
    },
    async UpdateCustomerSetting() {
        const response = await post<SettingRequest, CustomerInfo>('account/UpdateCustomerInfo');
        return formatCustomer(response.data);
    },
    async getLiveStream(matchId: number, liveStreamType: LiveStreamResource, streamId = 0) {
        const response = await get<LiveStreamGetLiveStreamUrlParams, LiveStreamResponse>('liveStream/getLiveStreamUrl', {
            params: {
                matchId,
                streamId,
                liveStreamType: LiveStreamResource.toPollux(liveStreamType),
            },
        });
        return formatLiveStream(response.data);
    },
    async getMyMatchIds() {
        const response = await get<never, number[]>('account/myMatchIds');
        return response.data;
    },
    async getMatchResults(sportType: SportType, resultDate: string) {
        const response = await get<MatchResultGetMatchResultsParams, MatchResponse[]>('matchResult/getMatchResults', {
            params: {
                sportType: SportType.toPollux(sportType),
                resultDate,
            },
        });
        return formatResults(sportType, response.data);
    },
    async getBetBuilderResults(sportType: SportType, matchId: number) {
        const response = await get<MatchResultGetBetBuilderResultParams, BetBuilderResultResponse>('matchResult/getBetBuilderResult', {
            params: {
                sportType: SportType.toPollux(sportType),
                matchId,
            },
        });
        return formatBetBuilderResults(response.data.marketResults);
    },
    async getDynamicMarketResults(sportType: SportType, matchId: number) {
        const response = await get<MatchResultGetDynamicMarketResultParams, DynamicMatchMarketResultResponse>('matchResult/getDynamicMarketResult', {
            params: {
                sportType: SportType.toPollux(sportType),
                matchId,
            },
        });
        return formatDynamicMarketResults(response.data.results);
    },

    async getDynamicMarketResultBySelection(sportType: SportType, matchId: number, selectionId: number) {
        const response = await get<MatchResultGetDynamicMarketResultBySelectionParams, DynamicMarketResultBySelectionResponse>('matchResult/GetDynamicMarketResultBySelection', {
            params: {
                sportType: SportType.toPollux(sportType),
                matchId,
                selectionId,
            },
        });
        return formatDynamicMarketResultBySelection(response.data);
    },

    async getOutrightResults(sportType: SportType, startDate: string, endDate: string) {
        const response = await get<MatchResultGetOutrightTournamentResultsParams, TournamentResponse[]>('matchResult/getOutrightTournamentResults', {
            params: {
                sportType: SportType.toPollux(sportType),
                startDate,
                endDate,
            },
        });
        return formatOutrightResults(sportType, response.data);
    },
    async getLocation() {
        const { data } = await get<never, LocationResponse>('configuration/location');
        let countryCode: string;
        switch (localizationHelper.getBackdoor()) {
            case Backdoor.India:
                countryCode = 'IN';
                break;
            case Backdoor.Brazil:
                countryCode = 'BR';
                break;
            default:
                countryCode = data.countryCode ?? '--';
        }
        const result: ILocation = {
            countryCode,
            brand: BrandType.fromPollux(data.brand),
            isMainDomain: data.isMainDomain,
        };
        return result;
    },
    async cashOutGetByTransIds(transIds: number[]) {
        const response = await post<CashOutValuesRequest, CashOutValueResponse[]>('cashOut/getValues', {
            transactionIds: transIds,
        });
        return response.data.map(x => formatCashOutValue(x));
    },
    async cashOutGetByTransIdsMixParlay(transIds: number[]) {
        const response = await post<CashOutValuesRequest, CashOutValueResponse[]>('cashOut/getMpValues', {
            transactionIds: transIds,
        });
        return response.data.map(x => formatCashOutValue(x));
    },
    async cashOutSingle(transId: number, cashOutValue: number) {
        const response = await post<CashOutRequest, CashOutResponse>('cashOut/single', {
            transactionId: transId,
            cashOutValue,
            fullCashOutValue: cashOutValue,
        });
        return formatCashOut(response.data);
    },
    async cashOutMixParlay(transId: number, cashOutValue: number) {
        const response = await post<CashOutRequest, CashOutResponse>('cashOut/mixParlay', {
            transactionId: transId,
            cashOutValue,
            fullCashOutValue: cashOutValue,
        });
        return formatCashOut(response.data);
    },
    async partialCashOutSingle(transactionId: number, cashOutValue: number, fullCashOutValue: number) {
        const response = await post<CashOutRequest, CashOutResponse>('cashOut/partialSingle', {
            transactionId,
            cashOutValue,
            fullCashOutValue,
        });
        return formatCashOut(response.data);
    },
    async getBannerFromUmai(bannerRequest: BannerRequest) {
        const response = await post<BannerRequest, BannerInfo[]>('banner/getBanners', bannerRequest);
        return formatBanner(response.data);
    },
    async getBettingPageInfo() {
        const response = await get<never, ExchangeResponseOfCustomerInfoResponse>('exchange/getBettingPage');
        return formatBettingPageInfo(response.data);
    },
    async getExchangeCustomerInfo() {
        const response = await get<never, ExchangeResponseOfCustomerInfoResponse>('exchange/getCustomerInfo');
        return formatExchangeCustomerInfo(response.data);
    },
    async transferIn(request: TransferRequest) {
        const response = await post<TransferRequest, ExchangeResponseOfObject>('exchange/TransferIn', request);
        const result: ITransferRecord = {
            status: ExchangeTransferStatus.fromPollux(response.data.status),
        };
        return result;
    },
    async transferOut(request: TransferRequest) {
        const response = await post<TransferRequest, ExchangeResponseOfObject>('exchange/TransferOut', request);
        const result: ITransferRecord = {
            status: ExchangeTransferStatus.fromPollux(response.data.status),
        };
        return result;
    },
    async getJackpotToken() {
        const response = await get<never, string>('jackpot/token');
        return response.data;
    },
    async claimJackpot(poolType: JackpotPoolType, round: number) {
        const response = await post<ClaimJackpotRequest, PolluxTriggerResult>('jackpot/claim', {
            poolType: JackpotPoolType.ToPollux(poolType),
            round,
        });
        return TriggerResult.FromPollux(response.data);
    },
    async getFantasySelectionToken() {
        const response = await get<never, FantasySelectionTokenResponse>('fantasySelection/token/getToken');
        return response.data.token;
    },
    async placeFantasySelectionBet(request: PlaceFantasySelectionBetRequest) {
        const response = await post<PlaceFantasySelectionBetRequest, PlaceFantasySelectionBetResponse>('fantasySelection/betSlip/placeBet', request);
        return FantasySelectionOrderStatus.fromPollux(response.data.placeOrderStatus);
    },
    async placeFantasySelectionBets(request: PlaceFantasySelectionBetRequest[]) {
        const response = await post<PlaceFantasySelectionBetRequest[], PlaceFantasySelectionBetResponse[]>('fantasySelection/betSlip/placeBets', request);
        return response.data.map(x => FantasySelectionOrderStatus.fromPollux(x.placeOrderStatus));
    },
    async getFantasySelectionMyBets() {
        const response = await get<never, FantasySelectionMyBetsResponse>('fantasySelection/myBetList/myBetList');
        return formatFantasySelectionMyBets(response.data);
    },
    async getFantasySelectionMyBetDetail(refNo: string) {
        const response = await get<MyBetListMyBetDetailParams, FantasySelectionMyBetDetailResponse>('fantasySelection/myBetList/myBetDetail', {
            params: {
                refNo,
            },
        });
        return formatMyBetDetail(response.data);
    },
    async getFantasySelectionBetCount() {
        const response = await get<never, FantasySelectionBetCountResponse>('fantasySelection/myBetList/betCount');
        return formatFantasySelectionBetCount(response.data);
    },
    async getFantasySelectionSettledBets(winLostDate: string) {
        const response = await get<MyBetListSettledBetList2Params, FantasySelectionBetResponse[]>('fantasySelection/myBetList/settledBetList', {
            params: {
                winLostDate,
            },
        });
        return response.data.map(bet => formatFantasySelectionBet(bet));
    },
    async generateTelegramLink() {
        const response = await get<TelegramGenerateTelegramLinkParams, GenerateTelegramLinkResponse>('telegram/GenerateTelegramLink', {
            params: {
                TimezoneOffsetMinutes: new Date().getTimezoneOffset() * -1,
            },
        });
        return response.data;
    },
};
